import React, { Fragment } from "react";
import Page from "../components/page";
import { graphql } from "gatsby";
import {
  Body1,
  Heading1,
} from "@vorne/gatsby-theme-marketing/components/typography/typography";
import PageTitle from "@vorne/gatsby-theme-marketing/components/pagetitle/pageTitle";
import List, {
  ListItem,
} from "@vorne/gatsby-theme-marketing/components/list/v1";
import QuickLinks from "@vorne/gatsby-theme-marketing/components/quicklinks/quickLinks";
import Center from "@vorne/gatsby-theme-marketing/components/layout/center/center";
import Card from "@vorne/gatsby-theme-marketing/components/card/card";
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
} from "@vorne/gatsby-theme-marketing/components/table/table";
import Image from "@vorne/gatsby-theme-marketing/components/image/image";
import BannerAd from "@vorne/gatsby-theme-marketing/components/banner-ad/banner-ad";
import Link from "@vorne/gatsby-theme-marketing/components/link/link";
import Routes from "../routes";
import BigIdea from "@vorne/gatsby-theme-marketing/components/bigidea/bigIdea";
import Section from "@vorne/gatsby-theme-marketing/components/section/section";
import { Seo } from "@vorne/gatsby-theme-marketing/components/seo/v2";
import { OEEBanner } from "@vorne/gatsby-theme-marketing/components/banners";

const TEEP = ({ data }) => {
  return (
    <Page>
      <Section>
        <PageTitle>Calculate TEEP</PageTitle>
        <BigIdea>
          OEE reveals how much of your Planned Production Time is truly
          productive, but TEEP also takes Utilization into account to show how
          much potential you have to increase throughput with the equipment you
          already own.
        </BigIdea>
        <QuickLinks tag="h2" />
        <Heading1 id="whatIsTEEP">What Is TEEP?</Heading1>
        <Body1>
          TEEP (Total Effective Equipment Performance) is a performance metric
          that provides insights as to the true capacity of your manufacturing
          operation. It takes account both Equipment Losses (as measured by OEE)
          and Schedule Losses (as measured by Utilization).
        </Body1>
        <Image
          src={data.TEEPFormulaImage}
          alt="Image of TEEP formula multiplying Availability, Performance, Quality and Utilization"
          renderCaption={() => (
            <Fragment>
              TEEP is calculated by multiplying four factors: Availability,
              Performance, Quality, and Utilization.
            </Fragment>
          )}
        />
        <Body1>Let’s briefly contrast OEE and TEEP:</Body1>
        <List type="bullet">
          <ListItem>
            OEE measures the percentage of Planned Production Time that is truly
            productive.
          </ListItem>
          <ListItem>
            TEEP measures the percentage of All Time that is truly productive.
          </ListItem>
        </List>
        <Body1>
          If your TEEP score is 100% then you are making only Good Parts, as
          fast as possible, with no stops, around the clock (24/7). In other
          words, you have no Schedule Losses and no{" "}
          <Link url={Routes.OEE.SixBigLosses}>OEE Losses</Link>.
        </Body1>
        <Table align="L L L" verticalAlign="C C C" cellWidths="35 25 40">
          <TableBody>
            <TableRow>
              <TableCell backgroundColor="#6A6A6A" textColor="#FFFFFF">
                Plan Not Open
              </TableCell>
              <TableCell
                rowSpan="2"
                backgroundColor="#909090"
                textColor="#FFFFFF"
              >
                Schedule Losses
              </TableCell>
              <TableCell
                rowSpan="2"
                backgroundColor="#909090"
                textColor="#FFFFFF"
              >
                TEEP takes into account Schedule Losses.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell backgroundColor="#6A6A6A" textColor="#FFFFFF">
                Production Not Scheduled
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell backgroundColor="#FE5B2F" textColor="#FFFFFF">
                Setup and Adjustments
              </TableCell>
              <TableCell
                rowSpan="6"
                backgroundColor="#F2F2F2"
                textColor="#000000"
              >
                Six Big Losses
              </TableCell>
              <TableCell
                rowSpan="6"
                backgroundColor="#F2F2F2"
                textColor="#000000"
              >
                OEE takes into account the Six Big Losses, which map to OEE
                Losses as follows:
                <List type="bullet">
                  <ListItem textColor="#FE5B2F">Availability Loss</ListItem>
                  <ListItem textColor="#0281CC">Performance Loss</ListItem>
                  <ListItem textColor="#8B8AC9">Quality Loss</ListItem>
                </List>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell backgroundColor="#FE5B2F" textColor="#FFFFFF">
                Breakdowns
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell backgroundColor="#0281CC" textColor="#FFFFFF">
                Reduced Speed
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell backgroundColor="#0281CC" textColor="#FFFFFF">
                Small Stops
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell backgroundColor="#8B8AC9" textColor="#FFFFFF">
                Production Rejects
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell backgroundColor="#8B8AC9" textColor="#FFFFFF">
                Startup Rejects
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell backgroundColor="#00AA45" textColor="#FFFFFF">
                Fully Productive Time
              </TableCell>
              <TableCell backgroundColor="#D9D9D9" textColor="#000000">
                OEE and TEEP
              </TableCell>
              <TableCell backgroundColor="#D9D9D9" textColor="#000000">
                <Body1>
                  OEE is the ratio of Fully Productive Time to Planned
                  Production Time. It takes into account the Six Big Losses.
                </Body1>
                <Body1>
                  TEEP is the ratio of Fully Productive Time to All Time. It
                  takes into account Schedule Losses and Six Big Losses.
                </Body1>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Body1>
          OEE is improving. Great job! Or is it? Dig a little deeper and the
          picture is less clear. Most companies would not want to increase
          Availability by 5.0% at the expense of decreasing Quality by 4.5%.
        </Body1>
        <Heading1 id="calculationExample">Calculation Example</Heading1>
        <Body1>
          Now let’s work through a complete example using the preferred OEE
          calculation. Here is data recorded for the first shift:
        </Body1>
        <Center $horizontally>
          <Table align="L L">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Item</TableHeaderCell>
                <TableHeaderCell>Data</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>Shift Length</TableCell>
                <TableCell>8 hours (480 minutes)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Breaks</TableCell>
                <TableCell>(2) 15 minute and (1) 30 minute</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Downtime</TableCell>
                <TableCell>47 minutes</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Ideal Cycle Time</TableCell>
                <TableCell>1.0 seconds</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Total Count</TableCell>
                <TableCell>19,271 widgets</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Reject Count </TableCell>
                <TableCell>423 widgets</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Heading1 id="howIsTEEPCalculated">How Is TEEP Calculated?</Heading1>
          <Body1>TEEP is calculated as:</Body1>
          <Card>
            <Body1>TEEP = OEE × Utilization</Body1>
          </Card>
          <Body1>Utilization is calculated as:</Body1>
          <Card>
            <Body1>Utilization = Planned Production Time / All Time</Body1>
          </Card>
          <Body1>
            Here is a simple example, based on a manufacturing operation with a
            65% OEE score, that is running two 8-hour shifts per day, five days
            per week.
          </Body1>
          <Table align="L L L">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Item</TableHeaderCell>
                <TableHeaderCell>Value</TableHeaderCell>
                <TableHeaderCell>Calculation</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>OEE</TableCell>
                <TableCell>65.00%</TableCell>
                <TableCell>
                  <Link url={Routes.OEE.CalculatingOEE}>
                    How to calculate OEE
                  </Link>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Planned Production Time</TableCell>
                <TableCell>80 hours</TableCell>
                <TableCell>8 hours × 2 shifts × 5 days</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>All Time</TableCell>
                <TableCell>168 hours</TableCell>
                <TableCell>24 hours × 7 days</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Utilization</TableCell>
                <TableCell>47.62%</TableCell>
                <TableCell>80 hours / 168 hours</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>TEEP</TableCell>
                <TableCell>30.95%</TableCell>
                <TableCell>0.6500 × 0.4762</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Center>
        <Heading1 id="TEEPAndTheHiddenFactory">
          TEEP and the Hidden Factory
        </Heading1>
        <Body1>
          TEEP indicates how much capacity is waiting to be unlocked in your “
          <Link url={Routes.OEE.HiddenFactory}>hidden factory</Link>”. In other
          words, it shows how much potential you have to increase throughput
          with your current equipment. In many cases, reclaiming time from your
          hidden factory is a faster and less expensive alternative to
          purchasing new equipment.
        </Body1>
        <Body1>
          TEEP can also be used to get a sense of your potential sales capacity
          as it takes into account the full capacity of your manufacturing
          plant. Keep in mind though, that even a{" "}
          <Link url={Routes.OEE.WorldClassOEE}>
            world-class manufacturing plant
          </Link>{" "}
          operating around the clock typically achieves only 80% to 90%
          Utilization of total capacity.
        </Body1>
        <BannerAd url={Routes.VorneWebsiteRoute} />
        <Heading1 id="manufacturingCapacityAndEquipmentUtilization">
          Manufacturing Capacity and Equipment Utilization
        </Heading1>
        <Body1>
          Capacity can be defined as “the amount that can be produced”. From a
          discrete manufacturing perspective, we can define capacity as “the
          maximum number of parts that can be manufactured”. Capacity is
          fundamentally a part-based metric (e.g., our current capacity is
          24,000 red widgets per hour).
        </Body1>
        <Body1>
          Utilization can be defined as “how much something is used”. From a
          discrete manufacturing perspective, we can define utilization as “the
          proportion of time that manufacturing equipment is used”. Utilization
          is fundamentally a percentage-based metric (e.g., our current
          utilization is 47.62%).
        </Body1>
        <Heading1 id="teepAndLosses">TEEP and Losses</Heading1>
        <Body1>
          As stated above, TEEP takes into account both Schedule Losses and OEE
          Losses. Interestingly, losses can be viewed from three perspectives:
        </Body1>
        <List type="bullet">
          <ListItem>
            Part Units (we lost 1,000 units of potential production)
          </ListItem>
          <ListItem>Time Units (we lost two hours of production)</ListItem>
          <ListItem>
            Percentage Units (we lost 17% of our Planned Production Time)
          </ListItem>
        </List>
        <Body1>
          All three perspectives can be useful – depending on whether you are
          thinking in terms of sales/capacity (part units), labor/utilization
          (time units), or manufacturing performance (percentage units).
        </Body1>
        <Body1>
          Measuring TEEP reveals these losses in full so that you can begin to
          understand and utilize the entire capacity of your plant.
        </Body1>
        <Body1>
          To learn more about manufacturing losses, visit the{" "}
          <Link url={Routes.OEE.SixBigLosses}>Six Big Losses</Link>.
        </Body1>
        <OEEBanner />
      </Section>
    </Page>
  );
};

export default TEEP;

export function Head() {
  return (
    <Seo
      title="Calculate TEEP: Increase Equipment Utilization"
      description="Use the TEEP formula to measure machine utilization and improve Total Effective Equipment Performance."
    />
  );
}

export const query = graphql`
  query {
    TEEPFormulaImage: file(relativePath: { eq: "teep-formula-image.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, width: 916)
      }
    }
    TEEPTableImage: file(relativePath: { eq: "teep-table-image.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, width: 916)
      }
    }
  }
`;
